import { BlockPlaceholder } from '../types/blockTypes';

export class BlockInsertionService {
  
  /**
   * Cuenta cuántos {{bloque}} hay en el documento actual
   */
  static async countBlockPlaceholders(): Promise<number> {
    try {
      return await Word.run(async (context) => {
        const searchResults = context.document.body.search('{{bloque}}', {
          matchCase: false,
          matchWholeWord: true
        });
        
        searchResults.load('items');
        await context.sync();
        
        return searchResults.items.length;
      });
    } catch (error) {
      console.error('Error counting block placeholders:', error);
      return 0;
    }
  }

  /**
   * Obtiene todas las posiciones de {{bloque}} en el documento
   */
  static async getBlockPlaceholders(): Promise<BlockPlaceholder[]> {
    try {
      return await Word.run(async (context) => {
        const searchResults = context.document.body.search('{{bloque}}', {
          matchCase: false,
          matchWholeWord: true
        });
        
        searchResults.load('items');
        await context.sync();
        
        return searchResults.items.map((_, index) => ({
          index,
          content: '{{bloque}}',
          order: index + 1
        }));
      });
    } catch (error) {
      console.error('Error getting block placeholders:', error);
      return [];
    }
  }

  /**
   * Inserta un nuevo {{bloque}} en la posición del cursor
   * y guarda el documento en background
   */
  static async insertPlaceholderAtCursor(): Promise<{ 
    success: boolean; 
    order: number; 
    error?: string 
  }> {
    try {
      let newOrder = 1;

      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        
        // Buscar todos los bloques existentes
        const searchResults = context.document.body.search('{{bloque}}', {
          matchCase: false,
          matchWholeWord: true
        });
        
        searchResults.load('items');
        await context.sync();

        // Almacenamos los resultados de las comparaciones
        const comparisons = searchResults.items.map(blockRange => 
          blockRange.compareLocationWith(selection)
        );
        
        // Sincronizamos para obtener los valores de las comparaciones
        await context.sync();

        // Ahora ya podemos leer .value de cada comparación
        for (const location of comparisons) {
          if (location.value === "Before" || location.value === "AdjacentBefore") {
            newOrder++;
          }
        }

        // Insertar el nuevo bloque
        selection.insertText('{{bloque}}', Word.InsertLocation.replace);
        await context.sync();
      });

      return {
        success: true,
        order: newOrder
      };
    } catch (error) {
      console.error('Error inserting placeholder at cursor:', error);
      return {
        success: false,
        order: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Guarda el documento en background (sin mostrarlo al usuario)
   */
  static async saveDocumentInBackground(
    _workpillId: string,
    _executionId: string
  ): Promise<boolean> {
    try {
      // Importar StorageService dinámicamente para evitar circular deps
      const { StorageService } = await import('../../../shared/services/storageService');
      await StorageService.saveDocumentToSupabase({ allowConflictDialog: false });
      return true;
    } catch (error) {
      console.error('Error saving document in background:', error);
      return false;
    }
  }

  /**
   * Navega al placeholder en el índice especificado
   * @param index Índice del placeholder (0-based)
   * @returns true si tuvo éxito, false si no existe
   */
  static async navigateToPlaceholder(index: number): Promise<boolean> {
    try {
      return await Word.run(async (context) => {
        const searchResults = context.document.body.search('{{bloque}}', {
          matchCase: false,
          matchWholeWord: true
        });
        
        searchResults.load('items');
        await context.sync();
        
        if (index >= 0 && index < searchResults.items.length) {
          const targetRange = searchResults.items[index];
          
          // Seleccionar el placeholder
          targetRange.select(Word.SelectionMode.select);
          
          // Hacer scroll para que sea visible
          targetRange.load('text');
          await context.sync();
          
          console.log(`✅ Navegado a placeholder ${index + 1} de ${searchResults.items.length}`);
          return true;
        }
        
        console.warn(`⚠️ Índice ${index} fuera de rango (0-${searchResults.items.length - 1})`);
        return false;
      });
    } catch (error) {
      console.error('Error navigating to placeholder:', error);
      return false;
    }
  }

  /**
   * Navega al siguiente placeholder relativo a la posición actual del cursor
   * @returns Índice del placeholder navegado, o -1 si no hay siguiente
   */
  static async navigateToNextPlaceholder(): Promise<number> {
    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        const searchResults = context.document.body.search('{{bloque}}', {
          matchCase: false,
          matchWholeWord: true
        });
        
        selection.load('text');
        searchResults.load('items');
        await context.sync();
        
        if (searchResults.items.length === 0) {
          console.log('⚠️ No hay placeholders en el documento');
          return -1;
        }
        
        // Si hay placeholders, navegar al primero después de la selección actual
        // Por simplicidad, iteramos y seleccionamos el primero
        const firstPlaceholder = searchResults.items[0];
        firstPlaceholder.select(Word.SelectionMode.select);
        await context.sync();
        
        console.log(`✅ Navegado al primer placeholder`);
        return 0;
      });
    } catch (error) {
      console.error('Error navigating to next placeholder:', error);
      return -1;
    }
  }

  /**
   * Navega al placeholder anterior relativo a la posición actual del cursor
   * @returns Índice del placeholder navegado, o -1 si no hay anterior
   */
  static async navigateToPreviousPlaceholder(): Promise<number> {
    try {
      return await Word.run(async (context) => {
        const searchResults = context.document.body.search('{{bloque}}', {
          matchCase: false,
          matchWholeWord: true
        });
        
        searchResults.load('items');
        await context.sync();
        
        if (searchResults.items.length === 0) {
          console.log('⚠️ No hay placeholders en el documento');
          return -1;
        }
        
        // Navegar al último placeholder
        const lastIndex = searchResults.items.length - 1;
        const lastPlaceholder = searchResults.items[lastIndex];
        lastPlaceholder.select(Word.SelectionMode.select);
        await context.sync();
        
        console.log(`✅ Navegado al último placeholder`);
        return lastIndex;
      });
    } catch (error) {
      console.error('Error navigating to previous placeholder:', error);
      return -1;
    }
  }

  /**
   * Hace scroll y selecciona la posición actual del cursor
   * @returns true si tuvo éxito
   */
  static async scrollToCursor(): Promise<boolean> {
    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        
        // Obtener el rango final de la selección
        const endRange = selection.getRange(Word.RangeLocation.end);
        
        // Seleccionar ese punto final para hacer scroll y mantener el cursor visible
        endRange.select(Word.SelectionMode.end);
        await context.sync();
        
        console.log('✅ Scroll al cursor realizado');
        return true;
      });
    } catch (error) {
      console.error('Error scrolling to cursor:', error);
      return false;
    }
  }
}

